import { useReactiveVar } from '@apollo/client';
import { IconButton, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import ParkingContext from 'context/ParkingContext';
import useHasUnlockedCustomerSegment from 'hooks/useHasUnlockedCustomerSegment';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutAnimation, Text, TouchableOpacity, View } from 'react-native';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { country } from 'states/persistInStorage';
import styled from 'styled-components/native';
import { Divider } from 'styles/CommonLayoutStyles';
import { CenterRow, StyledWrapper } from 'styles/ContainerStyles';
import { Charger } from 'types/common';
import { AccessMethod, AccessType } from 'types/generatedSchemaTypes';
import { BoldMeta } from '../../tariff/TariffItem';
import ChevronUpDownIcon from '../ChevronUpDownIcon';
import { useUiStore } from 'stores/uiStore';

export type ChargerSection = {
  data: Charger[];
  chargingPriceText: string;
  customerSegmentUid: string;
  customerSegmentAccessType: AccessType;
  customerSegmentAccessMethod: AccessMethod;
  customerSegmentTitle: string;
};

const Header = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  padding-bottom: 5px;
  padding-top: 5px;
  margin-left: 20px;
  margin-right: 20px;
`;

const RedeemIcon = styled(View)`
  margin-right: 10px;
`;

const HeaderText = styled(Text)`
  font-size: 16px;
  font-weight: bold;
`;

const NotesText = styled(Text)`
  font-size: 14px;
  color: #49595e;
  margin-top: 2px;
`;

const renderChevronIcon =
  (sectionId: string, activeSectionId: string | null) => () => (
    <ChevronUpDownIcon
      sectionId={sectionId}
      activeSectionId={activeSectionId}
    />
  );

const ChargerSectionHeader: React.FC<{
  section: ChargerSection;
  sectionIndex: number;
  isSingleSegment: boolean;
  activeCustomerSegmentUid: string | null;
  setActiveCustomerSegmentUid: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  zoneCode?: string;
}> = ({
  section,
  sectionIndex,
  isSingleSegment,
  activeCustomerSegmentUid,
  setActiveCustomerSegmentUid,
  zoneCode,
}) => {
  const { t } = useTranslation();
  const countryCode = useReactiveVar(country);
  const { setSelectedEvCharger } = useContext(ParkingContext);
  const { isUnlockValid } = useHasUnlockedCustomerSegment(
    section.customerSegmentUid || '',
    countryCode,
    zoneCode
  );
  const isLocked =
    section.customerSegmentAccessType === 'LOCKED' && !isUnlockValid;

  const toggleSection = useCallback(
    (uid: string) => {
      setSelectedEvCharger(undefined);
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setActiveCustomerSegmentUid((prev) => (prev === uid ? null : uid));
    },
    [setSelectedEvCharger, setActiveCustomerSegmentUid]
  );

  const handleUnlockTariff = useCallback(
    (selectedSection: ChargerSection) => {
      if (zoneCode) {
        useParkzoneStore.getState().setUnlockTariffItem({
          tariffId: selectedSection?.customerSegmentUid || '',
          tariffName: selectedSection?.customerSegmentTitle || '',
          accessMethod:
            selectedSection?.customerSegmentAccessMethod ?? undefined,
          activeElementType: 'CHARGING',
          zoneCode,
        });
        // We need to select charger first to unlock the tariff (CPS-21885: New endpoint for validating customer segment unlocking code)
        setSelectedEvCharger(selectedSection.data[0]);
        useUiStore.getState().setSheetModal('unlockTariff');
      }
    },
    [zoneCode, setSelectedEvCharger]
  );

  return (
    <>
      {sectionIndex !== 0 && <Divider size={8} mb={0} mt={0} />}
      <Header
        disabled={isLocked}
        onPress={() => {
          if (!isSingleSegment) {
            toggleSection(section.customerSegmentUid || '');
          }
        }}>
        <StyledWrapper
          mt={20}
          alignItems="flex-start"
          justifyContent="flex-start">
          <CenterRow isVerticalCenter={false}>
            {section.customerSegmentAccessType === 'LOCKED' && (
              <RedeemIcon>
                <Icon
                  color={
                    isLocked ? theme.colors.black : theme.colors.boost.green
                  }
                  name={isLocked ? 'Locked-Filled' : 'Unlocked-Filled'}
                />
              </RedeemIcon>
            )}
            <HeaderText>{section.customerSegmentTitle}</HeaderText>
          </CenterRow>
          {section.chargingPriceText && !isLocked && (
            <NotesText>{section.chargingPriceText}</NotesText>
          )}
        </StyledWrapper>
        {!isSingleSegment && !isLocked && (
          <IconButton
            buttonType="tertiary"
            onPress={() => toggleSection(section.customerSegmentUid || '')}
            rounded
            fullWidth={false}
            iconComponent={renderChevronIcon(
              section.customerSegmentUid || '',
              activeCustomerSegmentUid
            )}
            accessibilityLabel={t('information')}
          />
        )}
        {isLocked && (
          <TouchableOpacity onPress={() => handleUnlockTariff(section)}>
            <BoldMeta color={theme.colors.boost.green}>
              {t('parking.tariff.redeem')}
            </BoldMeta>
          </TouchableOpacity>
        )}
      </Header>
    </>
  );
};

export default ChargerSectionHeader;
